import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import CartSidebar from './CartSidebar';

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { getTotalItems } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="flex justify-around items-center h-16 relative" dir="rtl">
          {/* Store */}
          <Link
            to="/products"
            className={`flex flex-col items-center justify-center flex-1 h-full ${
              isActive('/products') ? 'text-primary' : 'text-gray-600'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <span className="text-xs mt-1">المتجر</span>
          </Link>

          {/* Orders */}
          <Link
            to="/orders"
            className={`flex flex-col items-center justify-center flex-1 h-full ${
              isActive('/orders') ? 'text-primary' : 'text-gray-600'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="text-xs mt-1">الطلبات</span>
          </Link>

          {/* Spacer for Home Button */}
          <div className="flex-[0.01]"></div>

          {/* Home Button (Center) */}
          <Link
            to="/"
            className="absolute left-1/2 transform -translate-x-1/2 -top-6"
          >
            <div className="w-16 h-16 rounded-full shadow-lg flex items-center justify-center bg-white hover:bg-gray-50 transition-colors p-2">
              <img 
                src="/icon.png" 
                alt="Logo" 
                className="w-full h-full object-contain"
              />
            </div>
          </Link>

          {/* Spacer for Home Button */}
          <div className="flex-1"></div>

          {/* Cart */}
          <button
            onClick={() => setIsCartOpen(true)}
            className={`flex flex-col items-center justify-center flex-1 h-full relative ${
              isActive('/cart') ? 'text-primary' : 'text-gray-600'
            }`}
          >
            <div className="relative">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {getTotalItems() > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                  {getTotalItems()}
                </span>
              )}
            </div>
            <span className="text-xs mt-1">السلة</span>
          </button>

          {/* Login/Profile */}
          <div className="relative flex-1 h-full flex items-center justify-center" ref={profileMenuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className={`flex flex-col items-center justify-center w-full h-full ${
                showProfileMenu ? 'text-primary' : isAuthenticated ? 'text-primary' : 'text-gray-600'
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-xs mt-1">{isAuthenticated ? 'حسابي' : 'دخول'}</span>
            </button>
            {showProfileMenu && isAuthenticated && (
              <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <button
                  onClick={() => {
                    navigate('/profile');
                    setShowProfileMenu(false);
                  }}
                  className="w-full text-right px-4 py-2 text-gray-700 hover:bg-gray-100 border-b border-gray-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  عرض الملف الشخصي
                </button>
                <button
                  onClick={() => {
                    logout();
                    setShowProfileMenu(false);
                  }}
                  className="w-full text-right px-4 py-2 text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  تسجيل الخروج
                </button>
              </div>
            )}
            {showProfileMenu && !isAuthenticated && (
              <Link
                to="/login"
                onClick={() => setShowProfileMenu(false)}
                className="absolute bottom-full right-0 mb-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50 px-4 py-2 text-right text-gray-700 hover:bg-gray-100 block"
              >
                تسجيل الدخول
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Cart Sidebar */}
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
};

export default MobileBottomNav;
